import React, { useMemo } from 'react';

import { NewspaperIcon } from '@heroicons/react/outline';
import { Link } from 'react-router-dom';
import { HiOutlineDownload } from 'react-icons/hi';
import { useApiGet } from 'react-reqq-lite';
import dayjs from 'dayjs';
import { FaRegFileAlt } from 'react-icons/fa';

import { PROFILE } from 'core-modules/profile/constants';
import BarChartSeries from 'modules/common/chart/BarChartSeries';
import HistogramCard, {
  useHistogramCard,
} from 'partial/dashboard/HistogramCard';
import DashboardSchemeCard, {
  useSchemeCard,
} from 'partial/dashboard/DashboardSchemeCard';
import CountCard from 'partial/dashboard/CountCard';
import Card from 'partial/components/Card';
import PageWrapper from 'partial/components/PageWrapper';
import Button from 'partial/components/Button';
import FormInlineSelect from 'partial/form/FormInlineSelect';
import { useDownloadFile } from 'partial/hooks';
import { generateYears, usePersistState } from 'helper';

import {
  useOrganizationCountCards,
  useOrganizationTransactionHistogram,
  useOrganizationTopBillers,
  useOrganizationTopBillerTypes,
  useOrganizationComparativeTransactions,
} from '../hooks';

const txnHistogramOptions = [
  {
    id: 'transaction',
    label: 'Total Transactions',
    hook: useOrganizationTransactionHistogram,
    height: 350,
    tableFormat: [
      {
        label: 'Month',
        key: 'label',
        id: 'label',
      },
      {
        label: 'Total Transaction',
        key: (row) => <span>{row?.count}</span>,
        id: 'count',
      },
    ],
    defaultChart: 'line',
    defaultMode: 'year',
    switchMode: false,
    minDate: '01/01/2020',
    maxDate: `${dayjs().format('MM/DD/YYYY')}`,
    seriesName: 'Transaction',
  },
];
const COMPARATIVE_ROWS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const COLORS = ['#D5D7E3', '#7E5EC5', '#B2D003'];

function OrganizationDashboardContainer() {
  const profile = useApiGet(PROFILE, {});
  const [downloadTop5Billers, isDownloadTop5Billers] = useDownloadFile({
    downloadUrl: `wallet/v1/biller_mgmt/organizations/${profile?.organization_id}/dashboard/top_billers_export`,
    fileName: `TOP_5_BILLERS_${dayjs().format('MM_DD_YYYY')}`,
  });
  const [downloadTopBillerTypes, isDownloadTopBillerTypes] = useDownloadFile({
    downloadUrl: `wallet/v1/biller_mgmt/organizations/${profile?.organization_id}/dashboard/export/top_biller_types`,
    fileName: `TOP_BILLER_TYPES_${dayjs().format('MM_DD_YYYY')}`,
  });
  const transactionHistogram = useHistogramCard(txnHistogramOptions);
  const [, tableTopBillerType] = useOrganizationTopBillerTypes();

  const schemeOptionsA = useMemo(
    () => [
      {
        pie: false,
        donut: true,
        id: 'top_billers',
        label: 'Top 5 Billers',
        hook: useOrganizationTopBillers,
        tableFormat: [
          {
            label: 'Rank',
            key: 'rank',
            id: 'rank',
          },
          {
            label: 'Total Transaction',
            key: 'total_transactions',
            id: 'total_transactions',
          },
          {
            label: 'Biller Name',
            key: 'biller_name',
            id: 'biller_name',
          },
        ],
        bottomActions: (
          <div className="flex justify-between items-center">
            <Link
              className="text-base text-primary-800 font-light"
              to="/transactions"
            >
              {`More Transactions >`}
            </Link>
            <Button
              size="sm"
              outlinePrimary
              icon={HiOutlineDownload}
              onClick={downloadTop5Billers}
              disabled={isDownloadTop5Billers}
            >
              Download CSV
            </Button>
          </div>
        ),
        defaultChart: 'table',
        donutChartOptions: {
          colors: ['#977ed1', '#00608B', '#F4BE43', '#72C78F', '#B0E2FF'],
          tooltip: {
            custom: ({ series, seriesIndex, w }) => {
              return `<div class="apexcharts-tooltip-content"><p class="apexcharts-tooltip-header"><span class="badge" style="background-color: ${
                w?.config?.colors[seriesIndex]
              }"></span>${w?.config?.labels[seriesIndex]}</p>
              <p>Rank: <span class="font-semibold">${seriesIndex + 1}</span></p>
              <p>Transactions: <span class="font-semibold">${
                series[seriesIndex]
              }</span></p>
              </div>`;
            },
            theme: 'light',
            y: {
              title: {
                formatter: () => '',
              },
            },
          },
        },
        barChartOptions: {
          yaxis: {
            labels: {
              align: 'left',
              formatter: (value, index) => {
                return `${index?.dataPointIndex + 1}. ${value}`;
              },
            },
          },
        },
      },
    ],
    [downloadTop5Billers, isDownloadTop5Billers]
  );
  const schemeOptionsB = [
    {
      id: 'biller_type',
      label: 'Top Biller Type',
      hook: useOrganizationTopBillerTypes,
      tableFormat: [
        {
          label: 'Rank',
          key: 'rank',
          id: 'rank',
        },
        {
          label: 'Total Transaction',
          key: 'total_transactions',
          id: 'total_transactions',
        },
        {
          label: 'Biller Type',
          key: 'biller_type',
          id: 'biller_type',
        },
      ],
      defaultSort: 'transaction:asc',
      pie: false,
      donut: true,
      bottomActions: (
        <div className="flex justify-between items-center">
          <Link
            className="text-base text-primary-800 font-light"
            to="/transactions"
          >
            {`More Transactions >`}
          </Link>
          <Button
            size="sm"
            outlinePrimary
            icon={HiOutlineDownload}
            onClick={downloadTopBillerTypes}
            disabled={isDownloadTopBillerTypes}
          >
            Download CSV
          </Button>
        </div>
      ),
      defaultChart: 'table',
      barChartOptions: {
        yaxis: {
          labels: {
            align: 'left',
            formatter: (value, index) => {
              return `${index?.dataPointIndex + 1}. ${value}`;
            },
          },
        },
      },
    },
  ];
  const donutChartOptionsB = useMemo(
    () => ({
      colors: ['#977ed1', '#00608B', '#F4BE43', '#72C78F'],
      tooltip: {
        custom: ({ series, seriesIndex, w }) => {
          return `<div class="apexcharts-tooltip-content">
        <p class="apexcharts-tooltip-header"><span class="badge" style="background-color: ${
          w?.config?.colors[seriesIndex]
        }"></span>${w?.config?.labels[seriesIndex]}</p>
        <p>Rank: <span class="font-semibold">${seriesIndex + 1}</span></p>
        <p>Transactions: <span class="font-semibold">${
          series[seriesIndex]
        }</span></p>
        <p>Amount: <span class="font-semibold">$${
          tableTopBillerType[seriesIndex]?.total_amount
        }</span></p>
        </div>`;
        },
        theme: 'light',
        y: {
          title: {
            formatter: () => '',
          },
        },
      },
    }),
    [tableTopBillerType]
  );

  const schemePropsA = useSchemeCard(schemeOptionsA);
  const schemePropsB = useSchemeCard(schemeOptionsB);
  const defaultStartYear = new Date().getFullYear();
  const defaultEndYear = new Date().getFullYear() + 2;
  const [compareFilter, setCompareFilter] = usePersistState(
    'comparative-filter',
    {
      year_start: defaultStartYear.toString(),
      year_end: defaultEndYear.toString(),
    },
    {
      keepOnUnmount: false,
    }
  );
  const [downloadComparative, isDownloadComparative] = useDownloadFile({
    downloadUrl: `wallet/v1/biller_mgmt/organizations/${profile?.organization_id}/dashboard/export/comparative_transaction?year_start=${compareFilter?.year_start}&year_end=${compareFilter?.year_end}`,
    fileName: `COMPARATIVE_TRANSACTION_BY_YEAR_${compareFilter?.year_start}-${compareFilter?.year_end}`,
  });
  const compareActions = useMemo(() => {
    const yearsStart = generateYears(
      compareFilter?.year_start
        ? Number(compareFilter?.year_start)
        : new Date().getFullYear(),
      5,
      5
    );
    const yearStartOptions = yearsStart.map((year) => ({
      label: year.toString(),
      value: year.toString(),
    }));
    const yearsEnd = generateYears(
      compareFilter?.year_start
        ? Number(compareFilter?.year_start) + 1
        : new Date().getFullYear() + 1,
      0,
      1
    );
    const yearEndOptions = yearsEnd.map((year) => ({
      label: year.toString(),
      value: year.toString(),
    }));

    return (
      <div className="flex items-center space-x-2">
        <FormInlineSelect
          name="year_start"
          onChange={setCompareFilter}
          onInput={({ target }) => {
            setCompareFilter({
              year_start: target?.value,
              year_end: '',
            });
          }}
          value={compareFilter.year_start}
          options={[{ label: '', value: '' }, ...yearStartOptions]}
          label=""
        />
        <span>To</span>
        <FormInlineSelect
          name="year_end"
          onChange={setCompareFilter}
          value={compareFilter.year_end}
          options={[{ label: '', value: '' }, ...yearEndOptions]}
          label=""
        />
        <Button
          size="sm"
          primary
          icon={FaRegFileAlt}
          onClick={downloadComparative}
          disabled={
            !compareFilter?.year_start ||
            !compareFilter?.year_end ||
            isDownloadComparative
          }
        />
      </div>
    );
  }, [
    compareFilter,
    setCompareFilter,
    downloadComparative,
    isDownloadComparative,
  ]);
  const [, , graph] = useOrganizationComparativeTransactions(compareFilter);
  return (
    <PageWrapper className="space-y-5 pb-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-y-3 md:gap-y-0 gap-x-0 md:gap-x-8 h">
        <CountCard
          name="total_amount"
          label="Total Amount"
          icon={NewspaperIcon}
          hook={useOrganizationCountCards}
        />
        <CountCard
          name="total_transactions"
          label="Total Transactions"
          icon={NewspaperIcon}
          hook={useOrganizationCountCards}
        />
        <CountCard
          name="active_billers"
          label="Active Billers"
          icon={NewspaperIcon}
          hook={useOrganizationCountCards}
        />
        <CountCard
          name="inactive_billers"
          label="Inactive Billers"
          icon={NewspaperIcon}
          hook={useOrganizationCountCards}
        />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="col-span-1 xl:col-span-2">
          <HistogramCard {...transactionHistogram} />
        </div>
        <DashboardSchemeCard {...schemePropsA} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <DashboardSchemeCard
          {...schemePropsB}
          donutChartOptions={donutChartOptionsB}
        />
        <div className="col-span-1 xl:col-span-2">
          <Card
            label="Comparative Transaction List by Year"
            renderAction={compareActions}
          >
            <div className="flex flex-col pr-3 mb-4" style={{ height: 300 }}>
              <BarChartSeries
                labels={COMPARATIVE_ROWS}
                series={graph || []}
                colors={COLORS}
              />
            </div>
            <div className="flex justify-end items-center px-5 pb-5 sm:px-6">
              <Link
                className="text-base text-primary-800 font-light"
                to="/transactions"
              >
                {`More Transactions >`}
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </PageWrapper>
  );
}

export default OrganizationDashboardContainer;
